import React, { useCallback } from 'react'
import type { Location } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import type mixpanelClient from '@disco/api/mixpanel'
import {
  createInstantACappella,
  createInstantInstrumental,
} from '@disco/state-management/actions/ExplorerActions'

import type {
  AudioSeparationDialogState,
  AudioSeparationType,
} from 'types/audio-separation'
import type { Track } from 'types/track'

import { Box, DropdownMenu, Text, Flex, HoverCard } from 'components/ds'
import { Button } from 'components/ui'
import ActionTitleWithBadge from 'components/track-actions/action-title-with-badge'

import { useDispatch } from 'hooks/use-redux'

import styles from './audio-separation-menu-option.module.css'

type Props = {
  type: AudioSeparationType
  track: Track
  enabled: boolean
  isAutoTagged: boolean
  audioSeparationTermsAccepted: boolean
  location: Location | undefined
  closeMenu: () => void
  mixpanelContext: string
  mixpanel: typeof mixpanelClient
}

const CONFIG = {
  instrumental: {
    label: 'Instant Instrumental',
    pendingKey: 'instant_instrumental_pending' as const,
    eventName: 'Click Instant Instrumental',
    upsellText: 'Instant Instrumentals are part of our Discovery Suite add-on.',
  },
  vocals: {
    label: 'Instant A Cappella',
    pendingKey: 'instant_a_cappella_pending' as const,
    eventName: 'Click Instant A Cappella',
    upsellText: 'Instant A Cappellas are part of our Discovery Suite add-on.',
  },
} as const

const AudioSeparationMenuOption = ({
  type,
  track,
  enabled,
  isAutoTagged,
  audioSeparationTermsAccepted,
  location,
  closeMenu,
  mixpanelContext,
  mixpanel,
}: Props): React.ReactNode => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  // AudioSeparationType is 'instrumental' | 'vocals', which exactly matches
  // CONFIG's keys — no fallback needed. If a new type is added in the future,
  // TypeScript will catch it here rather than silently using the wrong config.
  const config = CONFIG[type]
  const isPending = track[config.pendingKey] === true

  const handleClick = useCallback(() => {
    if (isPending) {
      closeMenu()
      return
    }

    mixpanel.sendPureEvent(config.eventName, {
      Context: mixpanelContext,
      Menu: 'Track',
      'Track ID': track.id,
    })

    if (audioSeparationTermsAccepted) {
      dispatch(
        type === 'instrumental'
          ? createInstantInstrumental(track.id)
          : createInstantACappella(track.id),
      )
    } else {
      navigate(`/explorer/tracks/${track.id}/instant-instrumental`, {
        state: {
          // Fall back to '/' when the menu is opened without a location prop
          // (e.g., from a context that doesn't pass it). Navigating to root is
          // a safe default; the user can return to their previous page manually.
          returnTo: location?.pathname ?? '/',
          separationType: type,
          autoTagged: isAutoTagged,
        } satisfies AudioSeparationDialogState,
      })
    }
    closeMenu()
  }, [
    audioSeparationTermsAccepted,
    closeMenu,
    config.eventName,
    dispatch,
    isAutoTagged,
    isPending,
    location?.pathname,
    mixpanel,
    mixpanelContext,
    navigate,
    track.id,
    type,
  ])

  const handleSubscribeClick = useCallback(() => {
    mixpanel.sendPureEvent('Click button', {
      Context: mixpanelContext,
      Modal: 'Discovery Suite upgrade',
      Copy: 'Subscribe to Discovery Suite',
    })
  }, [mixpanel, mixpanelContext])

  if (!track?.permissions?.manage) return null

  const caption = isPending ? `${config.label} (Processing...)` : config.label
  const label =
    type === 'vocals' && !isPending ? (
      <ActionTitleWithBadge title={config.label} badgeText="New" />
    ) : (
      <Text size="2">{caption}</Text>
    )

  if (enabled) {
    return (
      <DropdownMenu.Item
        className={isPending ? styles.pending : undefined}
        onSelect={handleClick}
        aria-disabled={isPending}
        aria-busy={isPending || undefined}
        data-testid={`audio-separation-option-${type}`}
      >
        <Flex>{label}</Flex>
      </DropdownMenu.Item>
    )
  }

  return (
    <HoverCard.Root>
      <HoverCard.Trigger>
        <DropdownMenu.Item
          disabled
          onSelect={closeMenu}
          data-testid={`audio-separation-option-${type}`}
        >
          <Flex>{label}</Flex>
        </DropdownMenu.Item>
      </HoverCard.Trigger>
      <HoverCard.Content size="2" maxWidth="320px">
        {' '}
        <Box>
          <Text size="2">Upgrade to unlock</Text>
          <Text as="p" size="2" mb="2">
            {config.upsellText}
          </Text>
          <Button
            appearance="primary"
            caption="Subscribe to Discovery Suite"
            link="/settings/billing#discovery-suite"
            onClick={handleSubscribeClick}
          />
        </Box>
      </HoverCard.Content>
    </HoverCard.Root>
  )
}

export default AudioSeparationMenuOption
